import { useEffect, useRef, useState, type FormEvent } from "react";
import { SignupApiClient, SignupRequestError, verificationTokenFromFragment } from "../signup-api";

export function reviewBoostPilotVisible(
  environment: string, optIn: string | undefined, productionReleaseApproval?: string,
): boolean {
  if (optIn !== "true") return false;
  if (environment === "staging") return true;
  return environment === "production" && productionReleaseApproval === "AR_MP_RELEASE_APPROVED";
}

type VerificationState =
  | {kind:"verifying"}
  | {kind:"ready"; registrationTicket: string}
  | {kind:"invalid"}
  | {kind:"complete"; organizationSlug: string};

export function ReviewBoostSignupPilot() {
  const api = useRef(new SignupApiClient());
  const verifyPage = window.location.pathname === "/signup/verify";
  const started = useRef(false);
  const [verification, setVerification] = useState<VerificationState>({kind:"verifying"});
  const [requested, setRequested] = useState(false);
  const [email,setEmail] = useState("");
  const [displayName,setDisplayName] = useState("");
  const [organizationName,setOrganizationName] = useState("");
  const [password,setPassword] = useState("");
  const [busy,setBusy] = useState(false);
  const [error,setError] = useState<string|null>(null);

  useEffect(() => {
    if (!verifyPage || started.current) return;
    started.current = true;
    const token = verificationTokenFromFragment(window.location.hash);
    // Secret must not remain in location/history once the component has read it.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (!token) { setVerification({kind:"invalid"}); return; }
    void api.current.verify(token).then((result) => {
      setVerification({kind:"ready",registrationTicket:result.registrationTicket});
    }).catch(() => setVerification({kind:"invalid"}));
  },[verifyPage]);

  async function request(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); if (busy) return;
    setBusy(true);setError(null);
    try {
      await api.current.request(email.trim());
      setRequested(true);
    } catch (cause) {
      setError(cause instanceof SignupRequestError && cause.status === 503
        ? "El registro todavía no está habilitado."
        : "No pudimos procesar la solicitud. Intentá nuevamente.");
    } finally { setBusy(false); }
  }

  async function complete(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || verification.kind!=="ready") return;
    setBusy(true); setError(null);
    try {
      const result = await api.current.complete({
        registrationTicket:verification.registrationTicket,
        displayName,organizationName,password,
      });
      setPassword("");
      setVerification({kind:"complete",organizationSlug:result.organizationSlug});
    } catch (cause) {
      setError(cause instanceof SignupRequestError && cause.status === 400
        ? "El enlace venció o los datos no son válidos. Solicitá una nueva verificación."
        : "No se pudo crear la cuenta. Intentá nuevamente.");
    } finally {setBusy(false);}
  }

  return <main className="rb-signup" id="main-content">
    <div className="rb-signup-card">
      <a href="/login" className="rb-signup-brand">ClickDialog</a>
      <p className="rb-signup-eyebrow">Review Boost · Argentina</p>
      <h1>{verifyPage ? "Activá tu cuenta" : "Empezá a cuidar tus reseñas"}</h1>
      <p>Respuestas para Google Business Profile con aprobación humana y automatización controlada.</p>
      <p className="rb-signup-price">ARS 20.000 / mes <span>· Sin prueba gratis</span></p>
      {error && <p role="alert" className="rb-signup-error">{error}</p>}
      {!verifyPage && !requested && <form onSubmit={(event) => void request(event)}>
        <label htmlFor="rb-signup-email">Correo de trabajo</label>
        <input id="rb-signup-email" type="email" autoComplete="email" required maxLength={254}
          value={email} onChange={(event)=>setEmail(event.target.value)} />
        <button type="submit" disabled={busy}>{busy?"Procesando…":"Enviar enlace de verificación"}</button>
      </form>}
      {!verifyPage && requested && <div role="status" className="rb-signup-success">
        Si el correo puede registrarse, recibirás un enlace de verificación. Revisá también la carpeta de spam.
      </div>}
      {verifyPage && verification.kind==="verifying" && <p role="status">Verificando el enlace…</p>}
      {verifyPage && verification.kind==="invalid" && <div role="alert">
        <p>El enlace no es válido, ya fue utilizado o venció.</p>
        <a href="/signup">Solicitar un enlace nuevo</a>
      </div>}
      {verifyPage && verification.kind==="ready" && <form onSubmit={(event)=>void complete(event)}>
        <label htmlFor="rb-owner">Tu nombre</label>
        <input id="rb-owner" autoComplete="name" required maxLength={120} value={displayName}
          onChange={event=>setDisplayName(event.target.value)} />
        <label htmlFor="rb-business">Nombre del negocio</label>
        <input id="rb-business" autoComplete="organization" required maxLength={120} value={organizationName}
          onChange={event=>setOrganizationName(event.target.value)} />
        <label htmlFor="rb-secret">Elegí una contraseña</label>
        <input id="rb-secret" type="password" autoComplete="new-password" required minLength={12} maxLength={128}
          value={password} onChange={event=>setPassword(event.target.value)} />
        <p className="rb-signup-note">La cuenta se crea sin activar el producto. La contratación y el acceso se habilitan únicamente después de un pago verificado.</p>
        <button type="submit" disabled={busy}>{busy?"Creando cuenta…":"Crear mi cuenta"}</button>
      </form>}
      {verifyPage && verification.kind==="complete" && <div className="rb-signup-success" role="status">
        <p>Tu cuenta fue creada. Iniciá sesión para consultar el estado de contratación y conectar Google cuando esté habilitado.</p>
        <a href="/login?returnTo=%2Faccount">Iniciar sesión</a>
      </div>}
      <p className="rb-signup-note">Sin cobro inmediato durante el registro. Podés cancelar futuros cobros y conservar el acceso hasta el final del período pagado.</p>
    </div>
  </main>;
}
